import { useCallback, useEffect, useRef, useState } from "react";
import { readCityOrder, recheckCityOrder, type CityOrderReview } from "../../../api/cityOrders";

/** How a read ended. `stale` is a read a newer one has overtaken: its answer
 * is dropped, and the caller does nothing with it. */
export type Outcome = { kind: "ok"; review: CityOrderReview } | { kind: "failed" } | { kind: "stale" };

function fetchReview(file: File | null, orderId: number | null, boq: number | null): Promise<CityOrderReview> {
  if (file !== null) {
    return readCityOrder(file, boq);
  }
  if (orderId !== null) {
    return recheckCityOrder(orderId, boq);
  }
  return Promise.reject(new Error("There is no PO to read."));
}

/** The PO being read: the review, the file it came from, and a link to its
 * PDF. Reading a newly dropped file, or checking an order on file again from
 * its stored PDF, share one path; only the latest request may answer. */
export function useCityReading(orderId: number | null) {
  const [file, setFile] = useState<File | null>(null);
  const [review, setReview] = useState<CityOrderReview | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const latest = useRef(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const load = useCallback(
    async (source: File | null, boq: number | null): Promise<Outcome> => {
      const mine = ++latest.current;
      setReading(true);
      setError("");
      try {
        const found = await fetchReview(source, orderId, boq);
        if (mine !== latest.current) {
          return { kind: "stale" };
        }
        setFile(source);
        setReview(found);
        return { kind: "ok", review: found };
      } catch (cause) {
        if (mine !== latest.current) {
          return { kind: "stale" };
        }
        setReview(null);
        setError(cause instanceof Error ? cause.message : String(cause));
        return { kind: "failed" };
      } finally {
        if (mine === latest.current) {
          setReading(false);
        }
      }
    },
    [orderId],
  );

  // The dropped PDF opens from a local URL, let go when another is dropped or
  // the drawer closes. Where the browser cannot make one, there is no link.
  useEffect(() => {
    if (file === null || typeof URL.createObjectURL !== "function") {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(new Blob([file], { type: "application/pdf" }));
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pdfUrl = file !== null ? blobUrl : orderId !== null ? `/api/v1/customer-orders/${orderId}/document` : null;
  return { file, review, reading, error, pdfUrl, load };
}
